import { JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "antd";
import styled from "styled-components";
import { CardContainer } from "../../components/Card/CardContainer";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import Text from "../../components/Text/Text";
import Button from "../../components/Button/Button";
import color from "../../resource/color";
import { dateFormatter } from "../../utility/Formatter";
import { TierAuditLogItem } from "../../datasource/CustomerTierProgramDatasource";
import { describeAuditLevels } from "../ShopTier/tierAuditDescribe";

const DetailCard = styled.div`
  border: 1px solid ${color.border};
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const ChangeRow = styled.div`
  padding: 6px 0;

  & + & {
    border-top: 1px solid ${color.border};
  }
`;

function InfoField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Text fontFamily="Sarabun" fontSize={14} color="Text3">{label}</Text>
      <Text fontFamily="Sarabun" fontSize={16} fontWeight={600} color="Text2">{value}</Text>
    </div>
  );
}

// There is no get-by-id endpoint for tier_audit_log, so AuditLogTab passes the row through
// router state. A hard refresh loses it; the page then asks the user to go back.
function ShopTierAuditDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const record = (location.state as { record?: TierAuditLogItem } | null)?.record;

  const backToAuditLog = () => navigate("/ShopTier", { state: { tab: "audit" } });

  const levels = describeAuditLevels(record?.valueAfter ?? null) ?? [];
  const yearText = record?.programYear ? ` · ชุดสิทธิประโยชน์ปี ${record.programYear}` : "";

  return (
    <CardContainer>
      <PageTitleNested
        title={`แก้ไขสิทธิประโยชน์${yearText}`}
        showBack
        onBack={backToAuditLog}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ระดับร้านค้า", path: "/ShopTier" },
              { text: "Audit Log", path: "/ShopTier", isState: true, callState: backToAuditLog },
              { text: "รายละเอียดการแก้ไข", path: window.location.pathname },
            ]}
          />
        }
      />

      {!record ? (
        <div style={{ marginTop: 24 }}>
          <Text fontFamily="Sarabun" fontSize={14} color="Text3">
            ไม่พบข้อมูลรายการนี้ กรุณากลับไปเลือกจาก Audit Log อีกครั้ง
          </Text>
          <div style={{ marginTop: 16 }}>
            <Button typeButton="border-primary" title="กลับไป Audit Log" style={{ width: "auto" }} onClick={backToAuditLog} />
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 24 }}>
          <DetailCard>
            <Row gutter={32}>
              <Col>
                <InfoField label="วันที่" value={record.createdAt ? dateFormatter(record.createdAt, true) : "-"} />
              </Col>
              <Col>
                <InfoField label="ผู้ดำเนินการ" value={record.updatedBy || "-"} />
              </Col>
              <Col>
                <InfoField label="เหตุผล" value={record.remark || "-"} />
              </Col>
            </Row>
          </DetailCard>

          {levels.length === 0 ? (
            <Text fontFamily="Sarabun" fontSize={14} color="Text3">ไม่มีการเปลี่ยนแปลงของระดับในรายการนี้</Text>
          ) : (
            levels.map((level) => (
              <DetailCard key={level.heading}>
                <Text fontFamily="Sarabun" fontSize={16} fontWeight={700} color="Text2">{level.heading}</Text>
                <div style={{ marginTop: 8 }}>
                  {level.changes.map((change, idx) => (
                    <ChangeRow key={idx}>
                      <Text fontFamily="Sarabun" fontSize={14} color="Text3">{change}</Text>
                    </ChangeRow>
                  ))}
                </div>
              </DetailCard>
            ))
          )}
        </div>
      )}
    </CardContainer>
  );
}

export default ShopTierAuditDetailPage;
